import type { Snapshot, SensorAggregate, MomentMonth, HourlyMomentCell, RecordsFile, MonthDailyMeans } from "@weather-deck/shared";
import { el } from "./dom";
import { renderYoYChart } from "./chart";
import { buildMonthRanking } from "./monthRanking";
import { buildQuarterRanking } from "./quarterRanking";

const AGGREGATE_SENSORS = ["temperatur", "luftfeuchte", "luftdruck", "pm25", "pm10"] as const;
type AggregateSensor = typeof AGGREGATE_SENSORS[number];

export function getBerlinDoY(date: Date): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "numeric",
    day: "numeric"
  }).formatToParts(date);
  const y = parseInt(parts.find(p => p.type === "year")!.value, 10);
  const m = parseInt(parts.find(p => p.type === "month")!.value, 10);
  const d = parseInt(parts.find(p => p.type === "day")!.value, 10);
  const target = new Date(Date.UTC(y, m - 1, d));
  const start = new Date(Date.UTC(y, 0, 1));
  return Math.floor((target.getTime() - start.getTime()) / 86400000) + 1;
}

const META: Record<AggregateSensor, { label: string, icon: string, unit: string, key: string, color: string, borderDash?: number[] }> = {
  temperatur:  { label: "Temperature", icon: "🌡", unit: "°C", key: "temperature", color: "#0d9488" },
  luftfeuchte: { label: "Humidity",    icon: "💧", unit: "%",  key: "humidity", color: "#0d9488" },
  luftdruck:   { label: "Pressure",    icon: "⏲", unit: "hPa", key: "pressure", color: "#0d9488" },
  pm25:        { label: "PM2.5",       icon: "💨", unit: "µg/m³", key: "pm25", color: "#f59e0b", borderDash: [2, 2] },
  pm10:        { label: "PM10",        icon: "💨", unit: "µg/m³", key: "pm10", color: "#f59e0b", borderDash: [2, 2] }
};

export function classifyBadge(now: number | null, p10: number | null, p50: number | null, p90: number | null) {
  if (now == null || p10 == null || p50 == null || p90 == null || Number.isNaN(p50)) {
    return { text: "no baseline", color: "#6b7280" };
  }
  if (now < p10) return { text: "unusually low", color: "#3b82f6" };
  if (now < p50) return { text: "cooler/drier than typical", color: "#10b981" };
  if (now < p90) return { text: "warmer/more humid than typical", color: "#f59e0b" };
  return { text: "unusually high", color: "#ef4444" };
}

export function getBerlinMomentParts(date: Date): { month: number, day: number, hour: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Berlin", month: "numeric", day: "numeric", hour: "numeric", hour12: false
  }).formatToParts(date);
  const get = (t: string) => parseInt(parts.find(p => p.type === t)!.value, 10);
  return { month: get("month"), day: get("day"), hour: get("hour") % 24 };
}

const SRC_META: Record<string, { color: string, label: string }> = {
  own:    { color: "#0d9488", label: "Rooftop" },
  dwd:    { color: "#6b7280", label: "DWD München-Stadt (3 km)" },
  dwd_fl: { color: "#6b7280", label: "DWD Flughafen (24 km)" },
  sc:     { color: "#f59e0b", label: "Citizen sensor" }
};

function ordinalRank(rank: number, superlative: string): string {
  if (rank === 1) return superlative;
  const suffix = rank === 2 ? "nd" : rank === 3 ? "rd" : "th";
  return `${rank}${suffix}-${superlative}`;
}

const MOMENT_FMT: Record<string, { digits: number, superlative: string }> = {
  temperatur:  { digits: 1, superlative: "warmest" },
  luftfeuchte: { digits: 0, superlative: "highest" },
  luftdruck:   { digits: 0, superlative: "highest" },
  pm25:        { digits: 1, superlative: "highest" },
  pm10:        { digits: 1, superlative: "highest" }
};

interface MomentEntry { year: number, val: number, src: string }
interface MomentStats { entries: MomentEntry[], min: number, max: number, median: number }

function cellStats(years: number[], cell: HourlyMomentCell): MomentStats | null {
  const entries = years
    .map((y, i) => ({ year: y, val: cell.vals[i], src: cell.src[i] }))
    .filter((e): e is MomentEntry => e.val != null);
  if (entries.length === 0) return null;
  const sorted = entries.map(e => e.val).sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median = sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
  return { entries, min: sorted[0]!, max: sorted[sorted.length - 1]!, median };
}

function rankOf(stats: MomentStats, live: number): number {
  return stats.entries.filter(e => e.val > live).length + 1;
}

function liveMomentValue(snap: Snapshot, sensor: AggregateSensor): number | null {
  const m = snap.sensor?.measurements;
  switch (sensor) {
    case "temperatur":  return m?.temperature ?? null;
    case "luftfeuchte": return m?.humidity ?? null;
    case "luftdruck":   return m?.pressure ?? null;
    case "pm25":        return m?.pm25 ?? snap.air?.measurements.pm25 ?? null;
    case "pm10":        return m?.pm10 ?? snap.air?.measurements.pm10 ?? null;
  }
}

export function buildMomentCard(snap: Snapshot, month: MomentMonth | null, day: number, hour: number): HTMLElement {
  const container = el("div", { className: "moment-card" });
  if (!month) return container;
  const getCell = (s: string) => month.sensors[s]?.[String(day)]?.[String(hour)];

  const tCell = getCell("temperatur");
  const tStats = tCell ? cellStats(month.years, tCell) : null;
  if (!tStats) return container;

  const monthName = new Intl.DateTimeFormat("en-US", { month: "short" })
    .format(new Date(Date.UTC(2024, month.month - 1, 15)));
  const hh = `${String(hour).padStart(2, "0")}:00`;
  container.append(el("h4", { text: `🕐 This moment in past years — ${monthName} ${day}, ${hh}` }));

  const now = liveMomentValue(snap, "temperatur");
  let summary = `Range since ${month.years[0]}: ${tStats.min.toFixed(1)} – ${tStats.max.toFixed(1)}°C · median ${tStats.median.toFixed(1)}°C`;
  if (now != null) {
    summary += ` — right now ${now.toFixed(1)}°C, the ${ordinalRank(rankOf(tStats, now), "warmest")} of ${tStats.entries.length + 1}`;
  }
  container.append(el("p", { className: "moment-summary", text: summary }));

  // Ranked warmest-first, with "Now" sitting in its own rank position rather
  // than pinned to the top — that is what makes the "warmest of 8" line above
  // checkable by eye. Ordering by year instead leaves the reader to sort eight
  // values in their head to verify the claim.
  const ranked: Array<{ label: string; val: number; color: string; title: string; isNow: boolean }> =
    tStats.entries.map((e) => {
      const src = SRC_META[e.src] ?? { color: "#6b7280", label: e.src };
      return { label: String(e.year), val: e.val, color: src.color, title: src.label, isNow: false };
    });
  if (now != null) {
    ranked.push({ label: "Now", val: now, color: "#0d9488", title: "Rooftop, live", isNow: true });
  }
  // Ties keep the live reading above the archived year of the same value.
  ranked.sort((a, b) => b.val - a.val || (a.isNow ? -1 : b.isNow ? 1 : 0));

  const list = el("div", { className: "moment-rows" });
  ranked.forEach((r, i) => {
    const row = el("div", { className: r.isNow ? "moment-row moment-now" : "moment-row" }, [
      el("span", { className: "moment-pos", text: `${i + 1}.` }),
      el("span", { className: "moment-dot", text: "●", title: r.title }),
      el("span", { className: "moment-year", text: r.label }),
      el("span", { className: "moment-val", text: `${r.val.toFixed(1)}°C` })
    ]);
    row.querySelector(".moment-dot")!.setAttribute("style", `color: ${r.color};`);
    list.append(row);
  });
  // Drives the column-major grid (see .moment-rows): half the entries, rounded
  // up, so column 1 holds the newest years and column 2 continues downward.
  list.setAttribute("style", `--moment-rows: ${Math.ceil(list.childElementCount / 2)};`);
  container.append(list);

  // one compact line per remaining sensor: now · rank · range · median
  const lines = el("div", { className: "moment-lines" });
  for (const s of AGGREGATE_SENSORS) {
    if (s === "temperatur") continue;
    const cell = getCell(s);
    const stats = cell ? cellStats(month.years, cell) : null;
    if (!stats) continue;
    const meta = META[s];
    const fmt = MOMENT_FMT[s]!;
    const u = meta.unit;
    const range = `range ${stats.min.toFixed(fmt.digits)} – ${stats.max.toFixed(fmt.digits)}${u} · median ${stats.median.toFixed(fmt.digits)}${u}`;
    const live = liveMomentValue(snap, s);
    const text = live != null
      ? `${meta.icon} ${meta.label} — now ${live.toFixed(fmt.digits)}${u} (${ordinalRank(rankOf(stats, live), fmt.superlative)} of ${stats.entries.length + 1}) · ${range}`
      : `${meta.icon} ${meta.label} — ${range}`;
    lines.append(el("div", { className: "moment-line", text }));
  }
  if (lines.childNodes.length > 0) container.append(lines);
  return container;
}

export function buildRecordsCard(records: RecordsFile | null): HTMLElement {
  const container = el("div", { className: "records-card" });
  if (!records || records.facts.length === 0) return container;

  container.append(el("h4", { text: "🏆 Records & stories — from your 7 sensors" }));
  const list = el("div", { className: "records-list" });
  for (const f of records.facts) {
    list.append(el("div", { className: "record-row" }, [
      el("span", { className: "record-icon", text: f.icon }),
      el("div", { className: "record-text" }, [
        el("div", { className: "record-headline", text: f.headline }),
        f.sub ? el("div", { className: "record-sub", text: f.sub }) : null
      ])
    ]));
  }
  container.append(list);
  container.append(el("p", { className: "record-note", text: records.note }));
  return container;
}

async function fetchRecords(): Promise<RecordsFile | null> {
  try {
    const r = await fetch("/aggregates/records.json", { cache: "force-cache" });
    if (!r.ok) return null;
    return await r.json() as RecordsFile;
  } catch { return null; }
}

async function fetchHourlyMoment(month: number): Promise<MomentMonth | null> {
  try {
    const r = await fetch(`/aggregates/moment_${String(month).padStart(2, "0")}.json`, { cache: "force-cache" });
    if (!r.ok) return null;
    return await r.json() as MomentMonth;
  } catch { return null; }
}

async function fetchMonthDaily(apiBase: string): Promise<MonthDailyMeans | null> {
  try {
    const r = await fetch(`${apiBase}/api/month-daily`, { cache: "no-store" });
    if (!r.ok || !(r.headers.get("content-type") ?? "").includes("application/json")) return null;
    return await r.json() as MonthDailyMeans;
  } catch { return null; }
}

async function fetchAggregate(sensor: AggregateSensor): Promise<SensorAggregate | null> {
  try {
    const r = await fetch(`/aggregates/${sensor}.json`, { cache: "force-cache" });
    if (!r.ok) return null;
    return await r.json() as SensorAggregate;
  } catch { return null; }
}

export function buildTodayVsTypical(snap: Snapshot, aggregates: Record<string, SensorAggregate | null>, doy: number): HTMLElement {
  const container = el("div", { className: "today-vs-typical" });

  const m = snap.sensor ? (snap.sensor.measurements as Record<string, number | null>) : null;

  for (const s of AGGREGATE_SENSORS) {
    const agg = aggregates[s];
    if (!agg) continue;

    const meta = META[s];
    let now: number | null = null;
    if (s === "pm25" || s === "pm10") {
      now = snap.air?.measurements[s] ?? null;
    } else {
      now = m ? (m[meta.key] ?? null) : null;
    }

    const idx = agg.seasonal_baseline.doy.indexOf(doy);
    const p10 = idx >= 0 ? agg.seasonal_baseline.p10[idx] : null;
    const p50 = idx >= 0 ? agg.seasonal_baseline.p50[idx] : null;
    const p90 = idx >= 0 ? agg.seasonal_baseline.p90[idx] : null;

    const hasBaseline = p10 != null && p50 != null && p90 != null && !Number.isNaN(p50);
    const badge = now == null && hasBaseline
      ? { text: "no live reading", color: "#6b7280" }
      : classifyBadge(now, p10 ?? null, p50 ?? null, p90 ?? null);
    
    const nowStr = now == null ? "—" : `${now.toFixed(1)}${meta.unit}`;
    
    const typStr = p10 == null || p90 == null || p50 == null || Number.isNaN(p50)
      ? "—" 
      : `${p10.toFixed(1)} – ${p90.toFixed(1)}${meta.unit} (median ${p50.toFixed(1)}${meta.unit})`;
    
    const formatter = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
    const todayLabel = formatter.format(new Date());

    const row = el("div", { className: "strip-row" }, [
      el("span", { className: "sensor-label", text: `${meta.icon} ${meta.label}` }),
      el("span", { className: "now-val", text: `NOW ${nowStr}` }),
      el("span", { className: "typ-val", text: `Typical for ${todayLabel}: ${typStr}` }),
      el("span", { className: "badge", text: badge.text })
    ]);
    row.querySelector(".badge")!.setAttribute("style", `background-color: ${badge.color}; color: white; padding: 4px 8px; border-radius: 12px; font-weight: 600; font-size: 0.75em; letter-spacing: 0.02em; text-transform: uppercase; margin-left: 8px;`);
    
    container.append(row);
  }
  return container;
}

export function dayLabel(date: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Berlin", day: "numeric", month: "long"
  }).format(date);
}

function buildYoYCharts(
  snap: Snapshot,
  aggregates: Record<string, SensorAggregate | null>,
  doy: number,
  now: Date
): HTMLElement {
  const container = el("div", { className: "yoy-charts" });
  const m = snap.sensor ? (snap.sensor.measurements as Record<string, number | null>) : null;
  const dateLabel = dayLabel(now);
  const currentYear = Number(new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Berlin", year: "numeric"
  }).format(now));

  for (const s of AGGREGATE_SENSORS) {
    const agg = aggregates[s];
    if (!agg) continue;

    const meta = META[s];
    let current: number | null = null;
    if (s === "pm25" || s === "pm10") {
      current = snap.air?.measurements[s] ?? null;
    } else {
      current = m ? (m[meta.key] ?? null) : null;
    }

    // The heading has to say which day this is: years along the axis otherwise
    // read as an annual-average trend rather than one calendar day.
    const chartWrapper = el("div", { className: "chart-wrapper" }, [
      el("h4", { text: `${meta.label} — ${dateLabel}, year by year` })
    ]);
    const canvas = document.createElement("canvas");
    chartWrapper.append(canvas);
    container.append(chartWrapper);

    renderYoYChart(canvas, agg, {
      doy, currentVal: current, currentYear,
      label: meta.label, unit: meta.unit, dateLabel,
      color: meta.color, borderDash: meta.borderDash
    });
  }

  if (container.childNodes.length > 0) {
    container.append(el("p", {
      className: "yoy-note",
      text: `Each point is that day's 24-hour mean on ${dateLabel} of that year — one day, not a yearly average, ` +
            `so year-to-year jumps are weather, not climate. The orange marker is a single live reading, which ` +
            `normally sits above a daily mean because the mean includes the night.`
    }));
  }

  return container;
}

export function buildClimateReportCard(agg: SensorAggregate | null): HTMLElement {
  const container = el("details", { className: "climate-card" }, [
    el("summary", { text: "Climate Report Card (Temperature)" })
  ]);
  if (!agg || !agg.heatwaves || !agg.frost_and_diurnal) return container;
  
  const years = Object.keys(agg.heatwaves).sort().reverse();
  
  // Short labels so six columns stay readable on a phone; the full wording
  // lives in the title attribute (and in the caption below the table).
  const COLUMNS: Array<{ short: string; full: string }> = [
    { short: "Year",     full: "Year" },
    { short: "Hot",      full: "Days above 25 °C" },
    { short: "V. hot",   full: "Days above 30 °C" },
    { short: "Longest",  full: "Longest unbroken run of days above 25 °C" },
    { short: "Frost",    full: "Days below 0 °C" },
    { short: "Diurnal",  full: "Mean daily temperature range, °C" }
  ];

  const thead = el("thead", {}, [
    el("tr", {}, COLUMNS.map((c) => el("th", { text: c.short, title: c.full })))
  ]);
  
  const tbody = el("tbody");
  for (const y of years) {
    const hw = agg.heatwaves[y];
    const fd = agg.frost_and_diurnal[y];
    if (!hw || !fd) continue;
    tbody.append(el("tr", {}, [
      el("td", { text: y }),
      el("td", { text: hw.days_above_25C.toString() }),
      el("td", { text: hw.days_above_30C.toString() }),
      el("td", { text: hw.longest_run_above_25C.toString() }),
      el("td", { text: fd.frost_days.toString() }),
      el("td", { text: fd.mean_diurnal_range_C.toFixed(1) })
    ]));
  }
  
  // Scroll container: .historical clips overflow, so without this the right
  // columns are simply unreachable on a narrow phone instead of scrollable.
  const table = el("table", {}, [thead, tbody]);
  container.append(el("div", { className: "climate-table-wrap" }, [table]));
  container.append(el("p", {
    className: "caption",
    text: "Hot = days above 25 °C · V. hot = above 30 °C · Longest = longest run above 25 °C · " +
          "Frost = below 0 °C · Diurnal = mean daily range. Swipe the table sideways for the later columns."
  }));
  container.append(el("p", { className: "caption", text: "Trend R² = 0.006 — 7 years is too short to detect climate trends. Numbers shown are year-over-year observations, not projections." }));

  return container;
}

function buildLegend(): HTMLElement {
  const div = el("div", { className: "legend" });
  div.innerHTML = `
    Data source: 
    <span style="color:#0d9488">●</span> Rooftop (Craig) &nbsp;
    <span style="color:#6b7280">●</span> DWD München-Stadt (3 km, pro) &nbsp;
    <span style="color:#6b7280; border-bottom: 1px dashed #6b7280">⌇</span> DWD Flughafen (24 km, pro) &nbsp;
    <span style="color:#f59e0b">⋯</span> Citizen sensor
  `;
  return div;
}

export function buildHistoricalCompare(snap: Snapshot, apiBase = ""): HTMLElement {
  const section = el("section", { className: "historical" });
  section.append(el("h3", { text: "Historical Context — since 2019" }));
  section.append(buildLegend());

  const body = el("div", { className: "historical-body", text: "Lade historische Daten …" });
  section.append(body);

  void hydrateHistorical(body, snap, apiBase);
  return section;
}

async function hydrateHistorical(body: HTMLElement, snap: Snapshot, apiBase: string): Promise<void> {
  const nowParts = getBerlinMomentParts(new Date());
  const momentPromise = fetchHourlyMoment(nowParts.month);
  const recordsPromise = fetchRecords();
  const monthDailyPromise = fetchMonthDaily(apiBase);
  const aggregates: Record<string, SensorAggregate | null> = {};
  await Promise.all(AGGREGATE_SENSORS.map(async (s) => {
    aggregates[s] = await fetchAggregate(s);
  }));
  const momentMonth = await momentPromise;
  const records = await recordsPromise;
  const monthDaily = await monthDailyPromise;

  const doy = getBerlinDoY(new Date());

  // Ordered live-first: what is happening now, then how this month and
  // quarter rank, then today against typical, then the charts. Records &
  // stories is all-time reference that changes maybe a few times a year, so
  // it sits down with the climate report card rather than interrupting the
  // live run.
  body.replaceChildren(); // clear placeholder
  body.append(buildMomentCard(snap, momentMonth, nowParts.day, nowParts.hour));
  body.append(buildMonthRanking(aggregates["temperatur"] ?? null, momentMonth, new Date(), monthDaily));
  body.append(buildQuarterRanking(aggregates["temperatur"] ?? null, new Date(), monthDaily));
  body.append(buildTodayVsTypical(snap, aggregates, doy));
  body.append(buildYoYCharts(snap, aggregates, doy, new Date()));
  body.append(buildRecordsCard(records));
  if (aggregates["temperatur"]) {
    body.append(buildClimateReportCard(aggregates["temperatur"]));
  }
}
